import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ClipboardList, CheckCircle2, AlertCircle, Clock, FileText, Plus,
  ListChecks, RefreshCw, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";

function Tile({ label, value, tone = "slate", icon: Icon }) {
  const toneClass = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    slate: "bg-white border-slate-200 text-slate-700",
  }[tone];
  return (
    <div className={`rounded-lg border p-4 ${toneClass}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <div className="text-3xl font-bold mt-2 font-mono">{value}</div>
    </div>
  );
}

const STATUS_LABEL = {
  open: { text: "Open", cls: "bg-blue-100 text-blue-800" },
  in_progress: { text: "In progress", cls: "bg-amber-100 text-amber-900" },
  pending_review: { text: "Pending review", cls: "bg-purple-100 text-purple-800" },
  completed: { text: "Completed", cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { text: "Cancelled", cls: "bg-slate-200 text-slate-700" },
  rejected: { text: "Rejected", cls: "bg-red-100 text-red-800" },
};

const PRIORITY_LABEL = {
  high: { text: "High", cls: "bg-red-100 text-red-800" },
  medium: { text: "Med", cls: "bg-amber-100 text-amber-900" },
  low: { text: "Low", cls: "bg-slate-100 text-slate-700" },
};

export default function EADashboard() {
  const [stats, setStats] = useState({});
  const [myTasks, setMyTasks] = useState([]);
  const [createdTasks, setCreatedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, mine, created] = await Promise.all([
        api.get("/delegation/stats").catch(() => ({ data: {} })),
        api.get("/delegation/tasks", { params: { view: "assigned_to_me" } }).catch(() => ({ data: [] })),
        api.get("/delegation/tasks", { params: { view: "created_by_me" } }).catch(() => ({ data: [] })),
      ]);
      setStats(s.data || {});
      setMyTasks(Array.isArray(mine.data) ? mine.data : []);
      setCreatedTasks(Array.isArray(created.data) ? created.data : []);
    } catch (e) {
      // swallow — keep UI alive
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Loading EA console…</div>;
  }

  // Compute summary if backend stats unavailable
  const myOpen = myTasks.filter(t => !["completed", "cancelled"].includes(t.status));
  const myHighPri = myOpen.filter(t => t.priority === "high");
  const myPendingReview = myTasks.filter(t => t.status === "pending_review");
  const createdOpen = createdTasks.filter(t => !["completed", "cancelled"].includes(t.status));

  return (
    <div data-testid="ea-dashboard" className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Console</div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Executive Assistant
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Delegation queue, action items, and quick links — everything you need to stay on top of leadership tasks.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" disabled={refreshing}
                  data-testid="ea-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Link to="/delegation/create">
            <Button size="sm" className="brand-bg brand-bg-hover text-white"
                    data-testid="ea-create-task-btn">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> New Task
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Assigned to me" value={myOpen.length}
              tone={myOpen.length ? "blue" : "slate"} icon={ClipboardList} />
        <Tile label="High priority" value={myHighPri.length}
              tone={myHighPri.length ? "red" : "slate"} icon={AlertCircle} />
        <Tile label="Pending review" value={myPendingReview.length}
              tone={myPendingReview.length ? "amber" : "slate"} icon={Clock} />
        <Tile label="Tasks I created" value={createdOpen.length}
              tone={createdOpen.length ? "emerald" : "slate"} icon={CheckCircle2} />
      </div>

      <Section title="My active tasks" subtitle="Newest first"
                 link="/delegation/tasks" linkLabel="View all tasks →">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Assigned by</th>
              <th className="text-left px-3 py-2">Due</th>
              <th className="text-left px-3 py-2">Priority</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {myOpen.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-xs text-emerald-700">
                Inbox zero ✓ No active tasks assigned to you.
              </td></tr>
            )}
            {myOpen.slice(0, 12).map(t => {
              const st = STATUS_LABEL[t.status] || STATUS_LABEL.open;
              const pr = PRIORITY_LABEL[t.priority] || PRIORITY_LABEL.medium;
              return (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`ea-task-${t.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/delegation/tasks/${t.id}`}
                          className="font-medium text-slate-900 hover:brand-text">
                      {t.title}
                    </Link>
                    {t.description && (
                      <div className="text-[11px] text-slate-500 truncate max-w-md">
                        {t.description}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {t.created_by_name || "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {t.due_date ? (
                      <span className={isOverdue(t.due_date) ? "text-red-700 font-semibold" : ""}>
                        {t.due_date}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${pr.cls}`}>
                      {pr.text}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.cls}`}>
                      {st.text}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <Section title="Tasks I've delegated" subtitle={`${createdOpen.length} active`}
                 link="/delegation/tasks" linkLabel="All my created tasks →">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Assignee</th>
              <th className="text-left px-3 py-2">Due</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {createdOpen.length === 0 && (
              <tr><td colSpan={4} className="text-center py-6 text-xs text-slate-500">
                No active delegated tasks.
              </td></tr>
            )}
            {createdOpen.slice(0, 8).map(t => {
              const st = STATUS_LABEL[t.status] || STATUS_LABEL.open;
              return (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`ea-created-${t.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/delegation/tasks/${t.id}`}
                          className="font-medium text-slate-900 hover:brand-text">
                      {t.title}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    {t.assignee_name || "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {t.due_date || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.cls}`}>
                      {st.text}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
        <QuickAction to="/delegation/tasks" icon={ClipboardList} label="All delegation tasks"
                       hint="Filter by status / assignee" />
        <QuickAction to="/delegation/create" icon={Plus} label="Create new task"
                       hint="Delegate to any employee/principal" />
        <QuickAction to="/admin/checklist" icon={ListChecks} label="Compliance Checklist"
                       hint="Approve/review recurring tasks" />
        <QuickAction to="/admin/requests" icon={FileText} label="Service Requests"
                       hint="Manage employee service tickets" />
      </div>
    </div>
  );
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  try {
    return new Date(dateStr) < new Date(new Date().toISOString().slice(0, 10));
  } catch {
    return false;
  }
}

function Section({ title, subtitle, link, linkLabel, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {title}
          </h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
        {link && (
          <Link to={link} className="text-xs brand-text hover:underline">
            {linkLabel || "View →"}
          </Link>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, hint }) {
  return (
    <Link to={to} data-testid={`ea-quick-${to.replace(/\//g, "-")}`}
          className="group bg-white border border-slate-200 rounded-lg p-4 hover:border-red-300 hover:bg-red-50/30 transition">
      <div className="flex items-start justify-between">
        <Icon className="h-5 w-5 text-slate-600 group-hover:brand-text" />
        <ArrowUpRight className="h-3.5 w-3.5 text-slate-400 group-hover:brand-text" />
      </div>
      <div className="text-sm font-semibold text-slate-900 mt-3">{label}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
    </Link>
  );
}
